import { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { Center, Divider, Spinner } from 'src/components/atoms';
import { Modal, Stepper } from 'src/components/molecules';
import { CancelModal } from 'src/components/molecules/CancelModal';
import { UploadActions, UsersUploadFile } from 'src/components/organisms';
import { usePreventNavigation } from 'src/hooks/navigation/usePreventNavigation';
import { useUploadUsers } from 'src/hooks/users';
import { PageContainer, Top } from './UploadUsers.styles';

export interface UploadUsersProps {
  orgExpiryPeriod: number;
}

export enum UploadScreen {
  UPLOAD,
  ERROR,
  ACTIVATE,
  DEACTIVATE,
}

export const UploadUsers = ({ orgExpiryPeriod = 0 }: UploadUsersProps) => {
  const {
    loading,
    redirect,
    setRedirect,
    screen,
    onImportSuccess,
    totalUsersInCSV,
    usersToCreate,
    usersToReinvite,
    usersToDeactivate,
    usersToUpdate,
    setScreen,
    commit,
    isValidated,
  } = useUploadUsers();
  const {
    showCancellationModal,
    preventNavigation,
    setNextLocation,
    onStay,
    onLeave,
    setShowCancellationModal,
    ref,
    setPreventNavigation,
  } = usePreventNavigation(
    screen === UploadScreen.ACTIVATE || screen === UploadScreen.DEACTIVATE,
    '/uploadUsers',
  );

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  useEffect(() => {
    if (isValidated) {
      if (usersToCreate.length + usersToReinvite.length > 0) {
        setScreen(UploadScreen.ACTIVATE);
      } else if (usersToDeactivate.length > 0) {
        setScreen(UploadScreen.DEACTIVATE);
      } else {
        setShowConfirmationModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    totalUsersInCSV,
    isValidated,
    usersToUpdate,
    usersToCreate,
    usersToReinvite,
    usersToDeactivate,
  ]);

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [ref, screen]);

  if (loading)
    return (
      <Center>
        <Spinner variant="secondary" size="xlarge" />
      </Center>
    );

  if (redirect) return <Redirect to="/users" />;
  switch (screen) {
    case UploadScreen.UPLOAD:
      return (
        <>
          <UsersUploadFile onSuccess={onImportSuccess} />
          <Modal
            open={showConfirmationModal}
            setOpen={setShowConfirmationModal}
            onConfirm={() => setShowConfirmationModal(false)}
            onCancel={() => setShowConfirmationModal(false)}
            size="medium"
            title="No changes needed"
            description="Your database of users is currently up-to-date."
          />
        </>
      );
    case UploadScreen.ACTIVATE:
    case UploadScreen.DEACTIVATE:
      return (
        <PageContainer ref={ref}>
          <CancelModal
            size="medium"
            title="Leave the CSV upload?"
            description="Your changes will not be saved"
            showCancellationModal={showCancellationModal}
            preventNavigation={preventNavigation}
            setNextLocation={setNextLocation}
            onConfirm={onStay}
            onCancel={onLeave}
            setOpen={setShowCancellationModal}
            setShowCancellationModal={setShowCancellationModal}
          />
          <Top>
            <Stepper
              activeStep={screen - 1}
              steps={['File upload', 'Activate users', 'Deactivate users']}
            />
            <Divider variant="horizontal" />
          </Top>
          <UploadActions
            screen={screen}
            totalUsersInCSV={totalUsersInCSV ?? 0}
            usersToCreate={usersToCreate}
            usersToReinvite={usersToReinvite}
            usersToDeactivate={usersToDeactivate}
            usersToUpdate={usersToUpdate}
            setScreen={setScreen}
            orgExpiryPeriod={orgExpiryPeriod}
            commit={commit}
            setShowCancellationModal={setShowCancellationModal}
            preventNavigation={preventNavigation}
            setPreventNavigation={setPreventNavigation}
            setRedirect={setRedirect}
          />
        </PageContainer>
      );
  }

  return null;
};
